import React from 'react';
import { Users } from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import { Popover, PopoverContent } from '../ui/popover';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { CompanyLogo } from '../ui/CompanyLogo';
import type { MentionOption } from './hooks/useMentions';

interface MentionDropdownProps {
  isOpen: boolean;
  options: MentionOption[];
  selectedIndex: number;
  loading: boolean;
  onSelect: (option: MentionOption) => void;
  textAreaRef: React.RefObject<HTMLTextAreaElement>;
}

const MentionDropdown: React.FC<MentionDropdownProps> = ({
  isOpen,
  options,
  loading,
  onSelect,
  textAreaRef,
}) => {
  const accounts = options.filter((opt) => opt.type === 'account');
  const contacts = options.filter((opt) => opt.type === 'contact');

  return (
    <Popover open={isOpen}>
      <PopoverPrimitive.Anchor asChild>
        <span
          ref={(el) => {
            // Anchor to the textarea position
            if (el && textAreaRef.current) {
              const rect = textAreaRef.current.getBoundingClientRect();
              el.style.position = 'fixed';
              el.style.left = `${rect.left + 12}px`;
              el.style.top = `${rect.top}px`;
              el.style.width = '0';
              el.style.height = '0';
            }
          }}
        />
      </PopoverPrimitive.Anchor>
      <PopoverContent
        side="top"
        align="start"
        sideOffset={12}
        className="w-[380px] p-0 shadow-popover"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search accounts and contacts..." />
          <CommandList>
            {loading && (
              <div className="px-4 py-3 text-sm text-muted-foreground flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                <span>Searching...</span>
              </div>
            )}

            {!loading && options.length === 0 && (
              <CommandEmpty>No accounts or contacts found.</CommandEmpty>
            )}

            {accounts.length > 0 && (
              <CommandGroup heading="Accounts">
                {accounts.map((option) => (
                  <CommandItem
                    key={option.id}
                    value={`account ${option.label} ${option.domain || ''}`}
                    onSelect={() => onSelect(option)}
                  >
                    <CompanyLogo
                      domain={option.domain || ''}
                      name={option.label}
                      className="h-5 w-5"
                    />
                    <span className="text-[13px]">{option.label}</span>
                    {option.domain && (
                      <span className="ml-auto text-[11px] text-muted-foreground">
                        {option.domain}
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {contacts.length > 0 && (
              <CommandGroup heading="Contacts">
                {contacts.map((option) => (
                  <CommandItem
                    key={option.id}
                    value={`contact ${option.label}`}
                    onSelect={() => onSelect(option)}
                  >
                    <div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                      <Users className="h-3 w-3 text-muted-foreground" />
                    </div>
                    <span className="text-[13px]">{option.label}</span>
                    <span className="ml-auto text-[11px] text-muted-foreground">
                      Contact
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default MentionDropdown;
